import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { GeneratingContentLoader } from '../../../components-v2/ai/GeneratingContentLoader';
import { Button } from '../../../components-v2/Button';
import { isRedrawable } from '../../../components-v2/UnifiedImageDialog/redrawableMedia';
import { CropIcon } from '../../svgs/icons/CropIcon';
import { EnlargeIcon } from '../../svgs/icons/EnlargeIcon';
import { GripVerticalIcon } from '../../svgs/icons/GripVerticalIcon';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { TrashIcon } from '../../svgs/icons/TrashIcon';
import { REVEAL_CLASS, styleSheet } from './styles';

const isVideoAttachment = (attachment: Api.IFileAttachment) =>
	!!attachment.mimeType?.toLowerCase().startsWith('video/');

type DragHandlers = Pick<
	React.HTMLAttributes<HTMLLIElement>,
	'onDragEnd' | 'onDragOver' | 'onDragStart' | 'onDrop' | 'onKeyDown'
>;

export interface IMediaTileProps extends DragHandlers {
	attachment: Api.IFileAttachment;
	draggable?: boolean;
	isDragging: boolean;
	isGenerating?: boolean;
	/** Dropping now would land the dragged tile at this tile's position. */
	isDropTarget: boolean;
	onCrop?: (attachment: Api.IFileAttachment) => void;
	onEditWithAI?: (attachment: Api.IFileAttachment) => void;
	onEnlarge?: (attachment: Api.IFileAttachment) => void;
	onRemove?: (attachment: Api.IFileAttachment) => void;
}

const TileAction: React.FC<{
	icon: React.ReactNode;
	isDestructive?: boolean;
	label: string;
	onClick: () => void;
	title: string;
}> = ({ icon, isDestructive, label, onClick, title }) => (
	<Button
		aria-label={label}
		onClick={onClick}
		size='icon'
		styles={[styleSheet.tileAction, ...(isDestructive ? [styleSheet.tileActionDestructive] : [])]}
		title={title}
		type='button'
		variant='ghost'
	>
		{icon}
	</Button>
);

const TileActions: React.FC<{
	attachment: Api.IFileAttachment;
	onCrop?: (attachment: Api.IFileAttachment) => void;
	onEditWithAI?: (attachment: Api.IFileAttachment) => void;
	onRemove?: (attachment: Api.IFileAttachment) => void;
}> = ({ attachment, onCrop, onEditWithAI, onRemove }) => (
	<div className={`${REVEAL_CLASS} ${css(styleSheet.tileActions)}`}>
		{onCrop ? (
			<TileAction
				icon={<CropIcon fill='currentColor' height={18} width={18} />}
				label={`Crop ${attachment.fileName}`}
				onClick={() => onCrop(attachment)}
				title='Crop'
			/>
		) : null}
		{onEditWithAI ? (
			<TileAction
				icon={<SparklesIcon height={18} width={18} />}
				label={`Edit ${attachment.fileName} with AI`}
				onClick={() => onEditWithAI(attachment)}
				title='Edit with AI'
			/>
		) : null}
		{onRemove ? (
			<TileAction
				icon={<TrashIcon fillColor='currentColor' height={18} width={18} />}
				isDestructive
				label={`Remove ${attachment.fileName}`}
				onClick={() => onRemove(attachment)}
				title='Remove'
			/>
		) : null}
	</div>
);

/**
 * One piece of the media on screen, carrying everything that can be done to it: reorder by dragging
 * the tile, enlarge from the thumbnail, and — revealed on hover or focus — crop, edit, and remove.
 */
export const MediaTile: React.FC<IMediaTileProps> = ({
	attachment,
	draggable,
	isDragging,
	isDropTarget,
	isGenerating,
	onCrop,
	onDragEnd,
	onDragOver,
	onDragStart,
	onDrop,
	onEditWithAI,
	onEnlarge,
	onKeyDown,
	onRemove,
}) => {
	const isVideo = isVideoAttachment(attachment);
	const redrawable = isRedrawable(attachment);
	const enlargesFromThumbnail = !isVideo && !!onEnlarge;

	const media = isVideo ? (
		<video className={css(styleSheet.tileVideo)} controls preload='metadata' src={attachment.url} />
	) : (
		// An image is a drag source of its own by default, which would drag the picture instead of
		// reordering the tile that holds it.
		<img alt='' className={css(styleSheet.tileImage)} draggable={false} src={attachment.url} />
	);

	return (
		<li
			className={css(
				styleSheet.tile,
				isVideo && styleSheet.tileWide,
				draggable && styleSheet.tileDraggable,
				isDropTarget && styleSheet.tileDropTarget,
				isDragging && styleSheet.tileDragging
			)}
			draggable={draggable}
			onDragEnd={onDragEnd}
			onDragOver={onDragOver}
			onDragStart={onDragStart}
			onDrop={onDrop}
			onKeyDown={onKeyDown}
			title={`${attachment.fileName} · ${getFileSizeStringValue(attachment.fileSize)}`}
		>
			{enlargesFromThumbnail ? (
				<Button
					aria-label={`Enlarge ${attachment.fileName}`}
					onClick={() => onEnlarge(attachment)}
					styles={[styleSheet.tileBody]}
					type='button'
					variant='ghost'
				>
					{media}
				</Button>
			) : (
				media
			)}
			{isGenerating ? <GeneratingContentLoader isGeneratingContent variant='tile' /> : null}
			{isVideo ? <span className={css(styleSheet.tileBadge)}>Video</span> : null}
			{draggable ? (
				<span aria-hidden='true' className={css(styleSheet.tileDragHandle)} data-media-drag-handle='true'>
					<GripVerticalIcon className={css(styleSheet.tileDragHandleIcon)} />
				</span>
			) : null}
			{enlargesFromThumbnail ? (
				<span aria-hidden='true' className={`${REVEAL_CLASS} ${css(styleSheet.tileZoomCue)}`}>
					<EnlargeIcon fill='currentColor' height={18} width={18} />
				</span>
			) : null}
			{onCrop || onEditWithAI || onRemove ? (
				<TileActions
					attachment={attachment}
					onCrop={onCrop && redrawable ? onCrop : undefined}
					onEditWithAI={onEditWithAI && redrawable ? onEditWithAI : undefined}
					onRemove={onRemove}
				/>
			) : null}
		</li>
	);
};
